import { useEffect, useMemo, useRef, useState } from 'react';

import { native } from '@muzly/shared';
import type { PitchTrackerEvent } from '@muzly/shared';

import {
  EVENT_CATEGORIES,
  type EventCategory,
  type LoggedEvent,
  appendEvent,
  initialEventLogState,
  isDiscrete,
} from './eventLog';

/**
 * 이벤트 로그/인스펙터 패널(#10) — 모든 `pitchTrackerEvent`를 롤링 버퍼로 모아
 * 타입 필터 + 이산 사건 색 하이라이트 + 행 클릭 JSON 인스펙터로 보여준다.
 * 일시정지/클리어, pitch_frame 폭주 대비 샘플링(eventLog.ts 순수 로직).
 */

const CATEGORY_TONE: Record<EventCategory, string> = {
  error: 'text-rose-600',
  interruption: 'text-amber-600',
  discontinuity: 'text-orange-600',
  pitch_frame: 'text-muted-foreground',
  session: 'text-sky-600',
  input_level: 'text-muted-foreground',
  performance: 'text-violet-600',
};

const num = (value: number, digits = 1): string =>
  Number.isFinite(value) ? value.toFixed(digits) : '—';

/** 행 한 줄 요약(타입별 핵심 필드). 렌더 중 throw 금지. */
const summarize = (event: PitchTrackerEvent): string => {
  switch (event.type) {
    case 'pitch_frame': {
      const f = event.frame;
      return f.voiced
        ? `${f.note ?? '—'} ${num(f.f0Hz ?? NaN)}Hz c${num(f.confidence, 2)}`
        : `unvoiced rms${num(f.rmsDb)}dB`;
    }
    case 'input_level':
      return `rms ${num(event.rmsDb)}dB`;
    case 'session_started':
      return event.session.sessionId;
    case 'session_stopped':
      return event.reason;
    case 'config_changed':
      return `algorithm=${event.config.analysis.algorithm}`;
    case 'stream_discontinuity':
      return `${event.info.reason}${event.info.gapMs != null ? ` gap${num(event.info.gapMs)}ms` : ''}`;
    case 'performance_metrics':
      return `p95 ${num(event.metrics.analysisP95Ms)}ms`;
    case 'interruption_started':
      return event.reason;
    case 'interruption_ended':
      return 'resumed';
    case 'error':
      return `${event.error.code}: ${event.error.message}`;
    default:
      return '';
  }
};

interface FilterChipsProps {
  enabled: Set<EventCategory>;
  onToggle: (category: EventCategory) => void;
}

const FilterChips = ({ enabled, onToggle }: FilterChipsProps) => (
  <div className="flex flex-wrap gap-1.5">
    {EVENT_CATEGORIES.map((category) => {
      const on = enabled.has(category);
      return (
        <button
          key={category}
          type="button"
          onClick={() => onToggle(category)}
          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
            on
              ? 'border-primary/40 bg-primary/10 text-foreground'
              : 'border-border/50 text-muted-foreground opacity-60'
          }`}
        >
          {category}
        </button>
      );
    })}
  </div>
);

interface EventRowProps {
  logged: LoggedEvent;
  selected: boolean;
  onSelect: (seq: number) => void;
}

const EventRow = ({ logged, selected, onSelect }: EventRowProps) => {
  const discrete = isDiscrete(logged.category);
  return (
    <button
      type="button"
      onClick={() => onSelect(logged.seq)}
      className={`flex w-full items-center gap-2 px-2 py-1 text-left font-mono text-[11px] ${
        selected ? 'bg-muted' : 'hover:bg-muted/50'
      } ${discrete ? 'font-semibold' : ''}`}
    >
      <span className={`w-24 shrink-0 ${CATEGORY_TONE[logged.category]}`}>
        {logged.type}
      </span>
      <span className="truncate text-muted-foreground">
        {summarize(logged.event)}
      </span>
    </button>
  );
};

const JsonInspector = ({ logged }: { logged: LoggedEvent }) => (
  <pre className="max-h-64 overflow-auto rounded-md border border-border bg-card p-2 font-mono text-[11px] leading-relaxed text-foreground">
    {JSON.stringify(logged.event, null, 2)}
  </pre>
);

export const EventLogPanel = () => {
  const [state, setState] = useState(initialEventLogState);
  const [paused, setPaused] = useState(false);
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const [enabled, setEnabled] = useState<Set<EventCategory>>(
    () => new Set(EVENT_CATEGORIES),
  );

  // pause 상태를 ref로 미러 — 구독 클로저가 stale paused를 읽지 않게 한다.
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(
    () =>
      native.on('pitchTrackerEvent', (event) => {
        if (pausedRef.current) {
          return;
        }
        setState((prev) => appendEvent(prev, event, Date.now()));
      }),
    [],
  );

  const toggle = (category: EventCategory): void =>
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });

  // 최신 우선 + 필터 적용.
  const visible = useMemo(
    () => state.events.filter((e) => enabled.has(e.category)).reverse(),
    [state.events, enabled],
  );
  const selected = state.events.find((e) => e.seq === selectedSeq) ?? null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FilterChips enabled={enabled} onToggle={toggle} />
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            className="rounded-md border border-border/60 px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted"
          >
            {paused ? '▶ 재개' : '⏸ 일시정지'}
          </button>
          <button
            type="button"
            onClick={() => {
              setState(initialEventLogState);
              setSelectedSeq(null);
            }}
            className="rounded-md border border-border/60 px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted"
          >
            클리어
          </button>
        </div>
      </div>

      <div className="max-h-64 overflow-auto rounded-md border border-border/40 bg-background">
        {visible.length === 0 ? (
          <p className="px-2 py-4 text-xs text-muted-foreground opacity-60">
            {paused
              ? '일시정지됨. 재개하면 이벤트를 수집합니다.'
              : '세션 시작 시 이벤트가 표시됩니다.'}
          </p>
        ) : (
          visible.map((logged) => (
            <EventRow
              key={logged.seq}
              logged={logged}
              selected={logged.seq === selectedSeq}
              onSelect={setSelectedSeq}
            />
          ))
        )}
      </div>

      <p className="text-[11px] text-muted-foreground opacity-60">
        {visible.length}건 표시 · pitch_frame {state.pitchFrameSeen}건
        수신(샘플링 적용)
      </p>

      {selected && <JsonInspector logged={selected} />}
    </div>
  );
};
